import { Component, Input, QueryList, ViewChildren } from '@angular/core';
import * as sortBy from 'lodash/sortBy';

import {
  ChangeType,
  TerminalProcedure,
  TerminalProcedureBatchFormat, TerminalProcedureCycle,
  TerminalProcedurePack, TerminalProcedureSet
} from '../models/terminal-procedure.model';
import { ProcedureComponent } from '../procedure/procedure.component';
import { AirportService, MAX_PROC_BATCH_LIMIT } from '../airport.service';


/**
 * Placeholder message to show when no terminal procedures are available yet.
 */
const NO_PROCS_AVAILABLE = 'Terminal procedures are not yet available for the selected data cycle. Please check back later.';


/**
 * Placeholder message to show when no terminal procedures have changed in the selected data cycle.
 */
const NO_PROCS_CHANGED = 'No terminal procedures have been changed in the selected data cycle.';


/**
 * Placeholder message to show when no terminal procedures have been deleted in the selected data cycle.
 */
const NO_PROCS_DELETED = 'No terminal procedures have been deleted in the selected data cycle.';


@Component({
  selector: 'app-procedure-listing',
  templateUrl: './procedure-listing.component.html',
  styleUrls: ['./procedure-listing.component.scss']
})
export class ProcedureListingComponent {
  @Input() identifier: string;
  @ViewChildren(ProcedureComponent) procedureComponents: QueryList<ProcedureComponent>;

  TerminalProcedureCycle = TerminalProcedureCycle;
  MAX_PROCEDURES = MAX_PROC_BATCH_LIMIT;
  TerminalProcedureBatchFormat = TerminalProcedureBatchFormat;

  private _hasProcs = false;
  private _cycle: TerminalProcedureCycle = TerminalProcedureCycle.Current;
  private _changedOnly = false;
  private _hideDeleted = false;
  private _procedurePack: TerminalProcedurePack;
  private _procedureSet: TerminalProcedureSet;
  private _allProcedures: Array<TerminalProcedure> = [];
  private _changedProcedures: Array<TerminalProcedure> = [];
  private _deletedProcedures: Array<TerminalProcedure> = [];
  private _procedures: Array<TerminalProcedure>;
  private _activeProcedures: Array<TerminalProcedure>;
  private _sortedProcedures: Array<TerminalProcedure>;
  private _effDateFrom: Date = null;
  private _effDateTo: Date = null;

  get hasProcs(): boolean {
    return this._hasProcs;
  }

  get procedurePack(): TerminalProcedurePack {
    return this._procedurePack;
  }

  @Input() set procedurePack(value: TerminalProcedurePack) {
    this._procedurePack = value;
    this._hasProcs = value.current.procedures.length > 0
      || (value.next && value.next.procedures.length > 0);
    this._resetSettings();
    this._updateProcedures();
  }

  get cycle(): TerminalProcedureCycle {
    return this._cycle;
  }

  set cycle(value: TerminalProcedureCycle) {
    this._cycle = value;
    this._changedOnly = false;
    this._hideDeleted = false;
    this._updateProcedures();
  }

  get effDateFrom(): Date {
    return this._effDateFrom;
  }

  get effDateTo(): Date {
    return this._effDateTo;
  }

  get changedOnly(): boolean {
    return this._changedOnly;
  }

  set changedOnly(value: boolean) {
    this._changedOnly = value;
    this._updateProcedures();
  }

  get hideDeleted(): boolean {
    return this._hideDeleted;
  }

  set hideDeleted(value: boolean) {
    this._hideDeleted = value;
    this._updateProcedures();
  }

  get allProcedures(): Array<TerminalProcedure> {
    return this._allProcedures;
  }

  get procedures(): Array<TerminalProcedure> {
    return this._procedures;
  }

  get anyChanged(): boolean {
    return this._changedProcedures.length > 0;
  }

  get changedOnlyTooltip(): string {
    return this.anyChanged ? null : NO_PROCS_CHANGED;
  }

  get anyDeleted(): boolean {
    return this._deletedProcedures.length > 0;
  }

  get hideDeletedTooltip(): string {
    return this.anyDeleted ? null : NO_PROCS_DELETED;
  }

  /**
   * Returns active terminal procedures sorted by type and name.
   * @returns {Array<TerminalProcedure>}
   */
  get sortedProcedures(): Array<TerminalProcedure> {
    return this._sortedProcedures;
  }

  get selectedProcedures(): Array<TerminalProcedure> {
    if (!this.procedureComponents) {
      return [];
    }
    return this.procedureComponents
      .filter(c => c.selected)
      .map(c => c.procedure);
  }

  get allSelected(): boolean {
    const procedures = this._changedOnly ? this._changedProcedures : this._activeProcedures;
    return this.selectedProcedures.length === procedures.length;
  }

  get downloadDisabled(): boolean {
    return this.selectedProcedures.length < 1 || this.selectedProcedures.length > MAX_PROC_BATCH_LIMIT;
  }

  get downloadTooltip() {
    const selected = this.selectedProcedures;
    if (selected.length < 1) {
      return 'Please select at least one procedure to download.';
    }
    if (selected.length > this.MAX_PROCEDURES) {
      return `Please select no more than ${this.MAX_PROCEDURES} procedures to download in a single batch.`;
    }
  }

  get placeholderMessage(): string {
    if (this._allProcedures.length === 0) {
      return NO_PROCS_AVAILABLE;
    }
    if (this._changedOnly && this._changedProcedures.length === 0) {
      return NO_PROCS_CHANGED;
    }
    return null;
  }

  constructor(private _airportService: AirportService) {}

  private _resetSettings() {
    this._cycle = TerminalProcedureCycle.Current;
    this._changedOnly = false;
    this._hideDeleted = false;
  }

  private _updateProcedures() {
    this.clearSelection();
    this._procedureSet = this._procedurePack[this._cycle];
    this._allProcedures = this._procedureSet.procedures;
    this._activeProcedures = this._allProcedures.filter(p => p.changeType !== ChangeType.Deleted);
    this._changedProcedures = this._allProcedures
      .filter(p => p.changeType === ChangeType.Added || p.changeType === ChangeType.Changed);
    this._deletedProcedures = this._allProcedures.filter(p => p.changeType === ChangeType.Deleted);
    this._procedures = this._changedOnly
      ? this._changedProcedures
      : (this._hideDeleted ? this._activeProcedures : this._allProcedures);
    this._sortedProcedures = sortBy(this._procedures, [p => TerminalProcedure.formatProcedureType(p.type), 'name']);
    this._effDateFrom = this._procedureSet.effDateFrom;
    this._effDateTo = this._procedureSet.effDateTo;
  }

  clearSelection() {
    if (this.procedureComponents) {
      this.procedureComponents.forEach(c => {
        if (c.procedure.changeType !== ChangeType.Deleted) {
          c.selected = false;
        }
      });
    }
  }

  toggleAll() {
    const selected = !this.allSelected;
    this.procedureComponents.forEach(c => {
      if (c.procedure.changeType !== ChangeType.Deleted) {
        c.selected = selected;
      }
    });
  }

  download(format: TerminalProcedureBatchFormat): string {
    const selected = this.selectedProcedures;
    if (selected.length < 1) {
      return null;
    }
    if (selected.length > MAX_PROC_BATCH_LIMIT) {
      return null;
    }
    const url = this._airportService.getProcedureBatchUrl(this.selectedProcedures, format);
    window.open(url);
  }
}
